import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TimerFormDraft } from './timerPageSlice';
import { getCurrentIsoWeek } from '@/shared/utils/weekUtils';

const emptyDraft: TimerFormDraft = {
  projectId: null,
  taskId: null,
  tagIds: [],
  description: '',
  billableOverride: null,
  date: null,
  startTime: null,
  endTime: null,
  // MNT-08 duration readout — carried for type parity; the weekly day-drill add
  // form is not yet implemented, so there is no surface for it here.
  duration: null,
};

export type WeeklyPageState = {
  viewedWeek: string;
  dayDrillOpen: boolean;
  dayDrillDate: string | null;
  dayDrillProjectId: string | null;
  activeForm: 'edit' | null;
  editingEntryId: string | null;
  formDraft: TimerFormDraft;
  submitInProgress: boolean;
  lastErrorKey: string | null;
  pendingDeleteEntryId: string | null;
};

const initialState: WeeklyPageState = {
  viewedWeek: getCurrentIsoWeek(),
  dayDrillOpen: false,
  dayDrillDate: null,
  dayDrillProjectId: null,
  activeForm: null,
  editingEntryId: null,
  formDraft: emptyDraft,
  submitInProgress: false,
  lastErrorKey: null,
  pendingDeleteEntryId: null,
};

export const weeklyPageSlice = createSlice({
  name: 'weeklyPage',
  initialState,
  reducers: {
    setViewedWeek(state, action: PayloadAction<string>) {
      state.viewedWeek = action.payload;
    },
    openDayDrill(
      state,
      action: PayloadAction<{ date: string; projectId: string }>,
    ) {
      state.dayDrillOpen = true;
      state.dayDrillDate = action.payload.date;
      state.dayDrillProjectId = action.payload.projectId;
    },
    closeDayDrill(state) {
      state.dayDrillOpen = false;
      state.dayDrillDate = null;
      state.dayDrillProjectId = null;
    },
    setWeeklyActiveForm(state, action: PayloadAction<'edit' | null>) {
      state.activeForm = action.payload;
    },
    setWeeklyEditingEntryId(state, action: PayloadAction<string | null>) {
      state.editingEntryId = action.payload;
    },
    setWeeklyFormProjectId(state, action: PayloadAction<string | null>) {
      state.formDraft.projectId = action.payload;
      state.formDraft.taskId = null;
      state.formDraft.tagIds = [];
      state.formDraft.billableOverride = null;
    },
    setWeeklyFormTaskId(state, action: PayloadAction<string | null>) {
      state.formDraft.taskId = action.payload;
    },
    setWeeklyFormTagIds(state, action: PayloadAction<string[]>) {
      state.formDraft.tagIds = action.payload;
    },
    setWeeklyFormDescription(state, action: PayloadAction<string>) {
      state.formDraft.description = action.payload;
    },
    setWeeklyFormBillableOverride(state, action: PayloadAction<boolean | null>) {
      state.formDraft.billableOverride = action.payload;
    },
    setWeeklyFormDate(state, action: PayloadAction<string | null>) {
      state.formDraft.date = action.payload;
    },
    setWeeklyFormStartTime(state, action: PayloadAction<string | null>) {
      state.formDraft.startTime = action.payload;
    },
    setWeeklyFormEndTime(state, action: PayloadAction<string | null>) {
      state.formDraft.endTime = action.payload;
    },
    setWeeklySubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setWeeklyLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    setWeeklyPendingDeleteEntryId(state, action: PayloadAction<string | null>) {
      state.pendingDeleteEntryId = action.payload;
    },
    prefillWeeklyEditForm(
      state,
      action: PayloadAction<{
        entryId: string;
        projectId: string;
        taskId: string | null;
        tagIds: string[];
        description: string;
        billable: boolean;
        date: string;
        startTime: string;
        endTime: string | null;
      }>,
    ) {
      const { entryId, projectId, taskId, tagIds, description, billable, date, startTime, endTime } =
        action.payload;
      state.editingEntryId = entryId;
      state.activeForm = 'edit';
      state.formDraft.projectId = projectId;
      state.formDraft.taskId = taskId;
      state.formDraft.tagIds = tagIds;
      state.formDraft.description = description;
      state.formDraft.billableOverride = billable;
      state.formDraft.date = date;
      state.formDraft.startTime = startTime;
      state.formDraft.endTime = endTime;
      state.lastErrorKey = null;
    },
    resetWeeklyEditForm(state) {
      state.activeForm = null;
      state.editingEntryId = null;
      state.formDraft = emptyDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    resetWeeklyPage() {
      return initialState;
    },
  },
});

export const {
  setViewedWeek,
  openDayDrill,
  closeDayDrill,
  setWeeklyActiveForm,
  setWeeklyEditingEntryId,
  setWeeklyFormProjectId,
  setWeeklyFormTaskId,
  setWeeklyFormTagIds,
  setWeeklyFormDescription,
  setWeeklyFormBillableOverride,
  setWeeklyFormDate,
  setWeeklyFormStartTime,
  setWeeklyFormEndTime,
  setWeeklySubmitInProgress,
  setWeeklyLastErrorKey,
  setWeeklyPendingDeleteEntryId,
  prefillWeeklyEditForm,
  resetWeeklyEditForm,
  resetWeeklyPage,
} = weeklyPageSlice.actions;

type AppRootState = { weeklyPage: WeeklyPageState };
export const selectWeeklyPage = (state: AppRootState) => state.weeklyPage;
